import { useState, useCallback } from "react";
import { integrationService } from "../services";
import { Integration, IntegrationSetting } from "../types";

interface UseIntegrationsReturn {
  // State
  integrations: Integration[];
  isLoading: boolean;
  error: Error | null;
  hasMore: boolean; // New: Indicates if there are more integrations to load

  // Integration operations
  loadIntegrations: (params?: {
    search?: string;
    category?: string;
    enabled?: boolean;
    page?: number; // New: For pagination/lazy loading
    pageSize?: number; // New: For pagination/lazy loading
    append?: boolean; // New: If true, append to existing integrations
  }) => Promise<Integration[]>;
  createIntegration: (integration: Omit<Integration, "id">) => Promise<void>;
  updateIntegration: (
    id: string,
    updates: Partial<Integration>,
  ) => Promise<void>;
  deleteIntegration: (id: string) => Promise<void>;
  enableIntegration: (id: string) => Promise<void>;
  disableIntegration: (id: string) => Promise<void>;

  // Integration data
  updateIntegrationSettings: (
    id: string,
    settings: IntegrationSetting[] | Record<string, unknown>,
  ) => Promise<void>;
  validateSettings: (
    id: string,
    settings: Record<string, unknown>,
  ) => Promise<{ valid: boolean; errors?: Record<string, string> }>;
  testConnection: (
    id: string,
  ) => Promise<{ success: boolean; message?: string }>;

  // Utility functions
  filterIntegrationsByKeywords: (keywords: string[]) => Integration[];
  sortIntegrations: () => void;
}

export function useIntegrations(): UseIntegrationsReturn {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true); // New: State for lazy loading

  const loadIntegrations = useCallback(async (params?: {
    search?: string;
    category?: string;
    enabled?: boolean;
    page?: number;
    pageSize?: number;
    append?: boolean;
  }) => {
    setIsLoading(true);
    setError(null);
    try {
      // Assuming integrationService.getIntegrations now accepts page/pageSize and returns hasMore
      const response = await integrationService.getIntegrations(params);
      const newIntegrations = response.data;
      const fetchedHasMore = response.hasMore; // Assuming response has a hasMore property

      setIntegrations((prev) => {
        const dataToSet = params?.append ? [...prev, ...newIntegrations] : newIntegrations;
        return integrationService.sortIntegrations(dataToSet);
      });
      setHasMore(fetchedHasMore);
      return newIntegrations;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createIntegration = useCallback(
    async (integration: Omit<Integration, "id">) => {
      setIsLoading(true);
      setError(null);
      try {
        const newIntegration =
          await integrationService.createIntegration(integration);
        setIntegrations((prev) => {
          const updated = [...prev, newIntegration];
          return integrationService.sortIntegrations(updated);
        });
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const updateIntegration = useCallback(
    async (id: string, updates: Partial<Integration>) => {
      setIsLoading(true);
      setError(null);
      try {
        const updatedIntegration = await integrationService.updateIntegration(
          id,
          updates,
        );
        setIntegrations((prev) => {
          const updated = prev.map((i) =>
            i.id === id ? updatedIntegration : i,
          );
          return integrationService.sortIntegrations(updated);
        });
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const deleteIntegration = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await integrationService.deleteIntegration(id);
      setIntegrations((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const enableIntegration = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedIntegration = await integrationService.enableIntegration(id);
      setIntegrations((prev) =>
        prev.map((i) => (i.id === id ? updatedIntegration : i)),
      );
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const disableIntegration = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const updatedIntegration =
        await integrationService.disableIntegration(id);
      setIntegrations((prev) =>
        prev.map((i) => (i.id === id ? updatedIntegration : i)),
      );
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateIntegrationSettings = useCallback(
    async (id: string, settings: IntegrationSetting[] | Record<string, unknown>) => {
      setIsLoading(true);
      setError(null);
      try {
        const updatedIntegration =
          await integrationService.updateIntegrationSettings(id, settings);
        setIntegrations((prev) =>
          prev.map((i) => (i.id === id ? updatedIntegration : i)),
        );
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const validateSettings = useCallback(
    async (id: string, settings: Record<string, unknown>) => {
      setIsLoading(true);
      setError(null);
      try {
        return await integrationService.validateSettings(id, settings);
      } catch (err) {
        setError(err as Error);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const testConnection = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      return await integrationService.testConnection(id);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const filterIntegrationsByKeywords = useCallback(
    (keywords: string[]) => {
      return integrationService.filterIntegrationsByKeywords(
        integrations,
        keywords,
      );
    },
    [integrations],
  );

  const sortIntegrations = useCallback(() => {
    setIntegrations((prev) => integrationService.sortIntegrations([...prev]));
  }, []);

  return {
    integrations,
    isLoading,
    error,
    hasMore, // New: Return hasMore state
    loadIntegrations,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    enableIntegration,
    disableIntegration,
    updateIntegrationSettings,
    validateSettings,
    testConnection,
    filterIntegrationsByKeywords,
    sortIntegrations,
  };
}
